// Toggle — on/off switch per COMPONENTS §Catalog + §Per-primitive WCAG.
//
// A native <input type="checkbox" role="switch"> drives semantics + keyboard
// (Space) + focus; it's visually hidden (`sr-only peer`) behind a token-styled
// track + sliding knob. The whole control is wrapped in a <label> so the label
// text is part of the hit target. Brand-coloured track when on (Approach D
// small-accent — DESIGN-TOKENS §Color).
//
// Controlled: `checked` + `onChange(checked: boolean)`. Either pass a visible
// `label` OR an `ariaLabel` (WCAG 4.1.2 — the switch needs an accessible name).

import { useId, type ReactNode } from 'react';

export interface ToggleProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  /** Visible inline label (caller-supplied, i18n'd). */
  label?: ReactNode;
  /** Accessible name when there is no visible label. */
  ariaLabel?: string;
  disabled?: boolean;
  id?: string;
  name?: string;
  className?: string;
}

export function Toggle({
  checked,
  onChange,
  label,
  ariaLabel,
  disabled = false,
  id: idProp,
  name,
  className = '',
}: ToggleProps) {
  const reactId = useId();
  const id = idProp ?? reactId;

  return (
    <label
      className={`inline-flex items-center gap-2.5 min-h-6 ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'} ${className}`}
    >
      <span className="relative inline-flex shrink-0">
        <input
          type="checkbox"
          role="switch"
          id={id}
          name={name}
          checked={checked}
          disabled={disabled}
          aria-label={label ? undefined : ariaLabel}
          onChange={(e) => onChange(e.target.checked)}
          className="peer sr-only"
        />
        <span
          aria-hidden="true"
          className={
            'h-5 w-9 rounded-full transition-colors ' +
            'peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-[var(--color-primary-ring)] ' +
            (checked ? 'bg-[var(--color-primary-display)]' : 'bg-[var(--color-border-strong)]')
          }
        />
        <span
          aria-hidden="true"
          className={
            'pointer-events-none absolute start-0.5 top-0.5 h-4 w-4 rounded-full bg-[var(--color-bg-page)] shadow-raised transition-transform ' +
            // Knob slides toward the inline-end; translate-x is physical, so flip
            // its direction under RTL (start-anchored + dir-aware travel).
            (checked ? 'translate-x-4 rtl:-translate-x-4' : 'translate-x-0')
          }
        />
      </span>
      {label && <span className="text-sm text-[var(--color-text-primary)]">{label}</span>}
    </label>
  );
}
